import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import { PageHeader, DataTable, Pill } from "../components/Table";
import { PlugsConnected, PaperPlaneRight, CheckCircle, XCircle } from "@phosphor-icons/react";

export default function DeviceGateway() {
  const [status, setStatus] = useState(null);
  const [devices, setDevices] = useState([]);
  const [raw, setRaw] = useState("");
  const [result, setResult] = useState(null);
  const [conn, setConn] = useState(null);

  const load = () => api.get("/gateway/status").then((r) => setStatus(r.data));
  useEffect(() => {
    load();
    api.get("/devices").then((r) => setDevices(r.data.devices));
    api.get("/gateway/connection-info").then((r) => setConn(r.data)).catch(() => {});
    const t = setInterval(load, 6000);
    return () => clearInterval(t);
  }, []);

  const useImei = devices[0]?.imei;

  const sendRaw = async (packet) => {
    try {
      const r = await api.post("/gateway/raw", { raw: packet });
      setResult(r.data);
      toast.success(`Decoded via ${r.data.protocol} · ${r.data.recognized ? "vehicle matched" : "unknown IMEI"}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Decode failed");
    }
  };

  const sampleFor = (proto) => {
    const s = proto.sample || "";
    return useImei ? s.replace(/8696\d+/, useImei) : s;
  };

  const columns = [
    { key: "received_at", header: "Time", render: (r) => <span className="text-xs text-muted-foreground font-mono">{new Date(r.received_at).toLocaleTimeString()}</span> },
    { key: "protocol", header: "Protocol", render: (r) => <Pill color="#007AFF">{r.protocol}</Pill> },
    { key: "imei", header: "IMEI", render: (r) => <span className="font-mono text-xs">{r.imei}</span> },
    { key: "recognized", header: "Matched", render: (r) => r.recognized ? <CheckCircle size={16} weight="fill" color="#34C759" /> : <XCircle size={16} weight="fill" color="#FF9500" /> },
    { key: "pos", header: "Position", render: (r) => <span className="font-mono text-xs">{r.parsed.lat}, {r.parsed.lng}</span> },
    { key: "speed", header: "Speed", render: (r) => `${r.parsed.speed} km/h` },
    { key: "panic", header: "Panic", render: (r) => r.parsed.panic ? <Pill color="#FF3B30">PANIC</Pill> : "—" },
  ];

  return (
    <div className="p-5 space-y-4">
      <PageHeader title="Device Gateway" subtitle="Multi-brand AIS-140 protocol ingestion engine" />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="bg-card border border-border rounded-sm p-4">
          <div className="flex items-center gap-2 mb-3">
            <PlugsConnected size={20} weight="bold" className="text-[#34C759]" />
            <span className="font-display font-bold">TCP Gateway</span>
            <Pill color="#34C759">LISTENING</Pill>
          </div>
          <div className="text-sm space-y-1.5">
            <div className="flex justify-between"><span className="text-muted-foreground">Bind</span><span className="font-mono">{status?.host}:{status?.port}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Packets decoded</span><span className="font-mono">{status?.recent_count ?? 0}</span></div>
          </div>
          <p className="text-xs text-muted-foreground mt-3">Devices connect directly to your VPS IP on the brand TCP port and stream raw packets — no API key required. Packets are stored and decoded automatically. Use the tester below to relay a packet into this pipeline.</p>
        </div>

        <div className="lg:col-span-2 bg-card border border-border rounded-sm p-4">
          <div className="label-caps mb-2">Supported Protocols — click to send a sample</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            {(status?.protocols || []).map((p) => (
              <button key={p.name} data-testid={`protocol-${p.name}`} onClick={() => { setRaw(sampleFor(p)); sendRaw(sampleFor(p)); }}
                className="text-left border border-border rounded-sm p-3 hover:border-primary">
                <div className="font-semibold">{p.name}</div>
                <div className="font-mono text-[10px] text-muted-foreground break-all mt-1">{p.prefix}…</div>
              </button>
            ))}
          </div>
          <div className="mt-3">
            <label className="label-caps">Raw packet tester</label>
            <textarea data-testid="raw-input" value={raw} onChange={(e) => setRaw(e.target.value)} rows={2}
              placeholder={useImei ? `$GPSBULL,${useImei},19.07,72.87,46,120,1,0*` : "$GPSBULL,<imei>,lat,lng,speed,heading,ign,panic*"}
              className="mt-1 w-full bg-background border border-border rounded-sm px-2 py-2 text-sm font-mono" />
            <button data-testid="decode-btn" onClick={() => sendRaw(raw)} disabled={!raw}
              className="mt-2 flex items-center gap-1.5 bg-primary text-white text-sm font-semibold px-4 py-2 rounded-sm disabled:opacity-50">
              <PaperPlaneRight size={15} weight="fill" /> Decode & Ingest
            </button>
          </div>
          {result && (
            <div className="mt-3 bg-background border border-border rounded-sm p-3 text-xs font-mono">
              <span className="text-[#34C759]">{result.protocol}</span> → matched={String(result.recognized)} · lat={result.parsed.lat} lng={result.parsed.lng} spd={result.parsed.speed} ign={String(result.parsed.ignition)} panic={String(result.parsed.panic)}
            </div>
          )}
        </div>
      </div>

      {conn && (
        <div className="bg-card border border-border rounded-sm p-4" data-testid="ingestion-guide">
          <div className="label-caps mb-2 flex items-center gap-1.5"><PlugsConnected size={13} /> Connect Your Device</div>
          <p className="text-xs text-muted-foreground mb-3">Configure each GPS tracker to send to your server host <code className="text-primary">{conn.host}</code> on its brand's port below. Each brand listens on its own 5-digit TCP port.</p>
          <div className="text-[11px] bg-[#FF9500]/10 border border-[#FF9500]/40 text-[#FF9500] rounded-sm px-2.5 py-1.5 mb-3" data-testid="ingest-note">
            ⚠ The host shown here is the preview URL and is illustrative only — raw device TCP can't reach the preview. In production, use your public server IP. See the <a href="/deployment" className="underline font-semibold">Deployment Guide</a>.
          </div>
          {conn.brands.length === 0 ? (
            <p className="text-xs text-muted-foreground">No GPS brands yet — add one in Device Inventory → GPS Brands.</p>
          ) : (
            <div className="space-y-2">
              {conn.brands.map((b) => (
                <div key={b.name} className="bg-background border border-border rounded-sm p-2.5" data-testid={`ingest-${b.name}`}>
                  <div className="flex items-center gap-2 text-sm flex-wrap">
                    <span className="font-semibold">{b.name}</span>
                    <Pill color="#00c8dc">{conn.host}:{b.port}</Pill>
                    <span className="text-xs text-muted-foreground">{b.protocol}</span>
                    <button onClick={() => { navigator.clipboard?.writeText(`${conn.host}:${b.port}`); toast.success("Host:port copied"); }}
                      className="text-[10px] font-semibold px-2 py-0.5 border border-border rounded-sm hover:border-primary">Copy</button>
                  </div>
                  {b.sample && <div className="font-mono text-[10px] text-muted-foreground mt-1 break-all">expected format: {b.sample}</div>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div>
        <div className="label-caps mb-2">Recently Decoded Packets</div>
        <DataTable testid="gateway-recent" columns={columns} rows={status?.recent || []} empty="No packets yet — send a sample above" />
      </div>
    </div>
  );
}
